import { useState } from 'react';
import { X, Building, Smartphone, Loader2, ArrowRight } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { walletService } from '../../../../services/wallet/walletService';
import { useWallet } from '../../../../context/wallet/WalletContext';
import CustomInput from '../../../../components/forms/CustomInput';

const RedemptionModal = () => {
  const {
    isRedeemModalOpen,
    setIsRedeemModalOpen,
    walletData,
    userId,
    fetchWalletDetails
  } = useWallet();

  const maxAmount = walletData?.balance || 0;
  const [amount, setAmount] = useState(maxAmount && maxAmount >= 500 ? maxAmount : 500);
  const [accountType, setAccountType] = useState('vpa');
  const [isLoading, setIsLoading] = useState(false);

  // Bank details state
  const [bankName, setBankName] = useState('');
  const [ifsc, setIfsc] = useState('');
  const [accountNumber, setAccountNumber] = useState('');

  // UPI state
  const [vpa, setVpa] = useState('');

  if (!isRedeemModalOpen) return null;

  const handleClose = () => {
    setIsRedeemModalOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (amount < 500) {
      toast.error('Minimum redemption amount is ₹500');
      return;
    }
    if (amount > maxAmount) {
      toast.error('Amount exceeds available balance');
      return;
    }

    const data = {
      userId,
      userType: 'Agent',
      amount: Number(amount),
      accountType
    };

    if (accountType === 'vpa') {
      if (!vpa) return toast.error('Please enter UPI ID');
      data.vpa = vpa;
    } else {
      if (!bankName || !ifsc || !accountNumber) return toast.error('Please fill all bank details');
      data.bankDetails = { name: bankName, ifsc, account_number: accountNumber };
    }

    setIsLoading(true);
    try {
      await walletService.requestRedemption(data);
      toast.success('Redemption request submitted successfully! Funds will credit shortly.');
      fetchWalletDetails();
      handleClose();
    } catch (error) {
      console.error('Redemption error:', error);
      toast.error(error.response?.data?.message || 'Failed to process redemption');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-3 sm:p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-3xl w-full max-w-md max-h-[calc(100vh-32px)] overflow-y-auto shadow-2xl border border-slate-100 animate-in zoom-in-95 duration-200 flex flex-col">
        {/* Modal Header */}
        <div className="flex justify-between items-center p-4 sm:p-6 border-b border-slate-100 bg-slate-50/50 shrink-0 sticky top-0 bg-white/95 backdrop-blur-xs z-10">
          <div>
            <h2 className="text-base sm:text-lg font-bold text-slate-900">Redeem Commission Funds</h2>
            <p className="text-xs text-slate-500 mt-0.5">Transfer your earnings to your UPI or Bank</p>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 rounded-full bg-white border border-slate-200 hover:bg-slate-100 flex items-center justify-center text-slate-500 transition-colors cursor-pointer shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 sm:space-y-5 flex-1">
          {/* Amount Input */}
          <div>
            <div className="flex justify-between items-center mb-1.5">
              <label className="block text-xs font-bold text-slate-700 tracking-wide">
                Amount to Redeem (INR)
              </label>
              <span className="text-xs text-[#067978] font-semibold">
                Max: ₹{(maxAmount || 0).toLocaleString('en-IN')}
              </span>
            </div>
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 font-bold text-slate-400 text-base">
                ₹
              </span>
              <input
                type="number"
                min="500"
                max={maxAmount}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full pl-8 pr-4 py-2.5 sm:py-3 rounded-2xl border border-slate-200 focus:ring-2 focus:ring-[#067978]/20 focus:border-[#067978] outline-none transition-all text-lg sm:text-xl font-bold text-slate-900"
                required
              />
            </div>
            <p className="text-[11px] text-slate-400 mt-1">Minimum redemption amount: ₹500</p>
          </div>

          {/* Payout Channel Tabs */}
          <div className="space-y-2">
            <label className="block text-xs font-bold text-slate-700 tracking-wide">
              Select Payout Channel
            </label>
            <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
              <div
                onClick={() => setAccountType('vpa')}
                className={`cursor-pointer flex items-center justify-center gap-1.5 sm:gap-2 p-2.5 sm:p-3 rounded-2xl border-2 transition-all ${
                  accountType === 'vpa'
                    ? 'border-[#067978] bg-teal-50/60 text-[#067978] font-bold shadow-xs'
                    : 'border-slate-200 text-slate-600 hover:border-slate-300 font-medium'
                }`}
              >
                <Smartphone size={16} className="shrink-0" />
                <span className="text-xs">Instant UPI</span>
              </div>
              <div
                onClick={() => setAccountType('bank_account')}
                className={`cursor-pointer flex items-center justify-center gap-1.5 sm:gap-2 p-2.5 sm:p-3 rounded-2xl border-2 transition-all ${
                  accountType === 'bank_account'
                    ? 'border-[#067978] bg-teal-50/60 text-[#067978] font-bold shadow-xs'
                    : 'border-slate-200 text-slate-600 hover:border-slate-300 font-medium'
                }`}
              >
                <Building size={16} className="shrink-0" />
                <span className="text-xs">Bank Transfer</span>
              </div>
            </div>
          </div>

          {/* Dynamic Channel Fields */}
          {accountType === 'vpa' ? (
            <div className="space-y-3 pt-1">
              <CustomInput
                label="UPI ID (VPA)"
                type="text"
                value={vpa}
                onChange={(e) => setVpa(e.target.value)}
                placeholder="e.g. yourname@oksbi / 9876543210@paytm"
                required
              />
            </div>
          ) : (
            <div className="space-y-3 pt-1">
              <CustomInput
                label="Account Holder Name"
                type="text"
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                placeholder="e.g. Rajesh Sharma"
                required
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <CustomInput
                  label="IFSC Code"
                  type="text"
                  value={ifsc}
                  onChange={(e) => setIfsc(e.target.value.toUpperCase())}
                  placeholder="HDFC0001234"
                  mono={true}
                  required
                />
                <CustomInput
                  label="Account Number"
                  type="text"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  placeholder="123456789012"
                  mono={true}
                  required
                />
              </div>
            </div>
          )}

          <div className="pt-2 border-t border-slate-100">
            <button
              type="submit"
              disabled={isLoading || amount < 500 || amount > maxAmount}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-[#067978] hover:bg-[#05605f] text-white rounded-2xl font-bold text-xs shadow-md shadow-[#067978]/25 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin" size={16} />
                  <span>Processing Payout...</span>
                </>
              ) : (
                <>
                  <span>Transfer ₹{Number(amount || 0).toLocaleString('en-IN')} Now</span>
                  <ArrowRight size={14} />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RedemptionModal;
